/**
 * Metro 2 Technical Dispute Wizard - Complete Refactor
 * 
 * COMPLETELY ISOLATED from Factual Dispute flow.
 * Uses separate backend endpoints: /api/disputes/metro2/*
 * 
 * 5-Step Flow:
 * 1. AI Eligibility Triage + Account Selection
 * 2. Technical Context + Evidence
 * 3. Metro 2 Technical Review + AI Package Builder
 * 4. Review, Customize, and Validate Generation Plan
 * 5. Generate + Preview Final Metro 2 Letters
 * 
 * CANONICAL VALUES:
 * - family = "metro2"
 * - method = "metro2_technical"
 * 
 * BLOCKED FALLBACKS:
 * - NO factual, data_breach, personal_info, inquiry, structural, recovery, isolated, combo
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  ArrowLeft,
  Database,
  AlertTriangle,
  Check,
  ChevronDown,
  FileText,
  Download,
  Printer,
  RefreshCw,
  Lock,
  AlertCircle,
  CheckCircle,
  XCircle,
  Table,
  Sparkles,
  Shield,
  Zap,
  Settings,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardHeader, CardTitle, CardContent, CardDescription, CardFooter } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { useAuth } from '../../context/AuthContext';
import { useReportData } from '../../context/ReportDataContext';
import { toast } from 'sonner';

// Import Metro 2 Components
import Metro2EligibilityTriage from '../../components/disputes/Metro2EligibilityTriage';
import Metro2AccountCard from '../../components/disputes/Metro2AccountCard';
import Metro2BureauComparisonTable from '../../components/disputes/Metro2BureauComparisonTable';
import Metro2LetterPackageBuilder from '../../components/disputes/Metro2LetterPackageBuilder';
import Metro2PaymentHistoryReview from '../../components/disputes/Metro2PaymentHistoryReview';
import Metro2CollectionReviewPanel from '../../components/disputes/Metro2CollectionReviewPanel';
import Metro2ChargeOffReviewPanel from '../../components/disputes/Metro2ChargeOffReviewPanel';
import Metro2StudentLoanPanel from '../../components/disputes/Metro2StudentLoanPanel';
import Metro2LegalBasisSummary from '../../components/disputes/Metro2LegalBasisSummary';
import Metro2FieldMappingSummary from '../../components/disputes/Metro2FieldMappingSummary';
import RoutedDisputeItemsPanel from '../../components/disputes/RoutedDisputeItemsPanel';
import Metro2Step2Evidence from '../../components/disputes/Metro2Step2Evidence';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// ============================================================================
// CONSTANTS
// ============================================================================

// Canonical Metro 2 values - MUST be included in all requests
const METRO2_CANONICAL = {
  dispute_type: 'metro2',
  family: 'metro2',
  method: 'metro2_technical',
};

// Bureau colors matching FundElite design
const BUREAU_COLORS = {
  TransUnion: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700',
  Experian: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700',
  Equifax: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700',
};

// 5-Step wizard flow
const STEPS = [
  { label: 'Select', shortLabel: '1', description: 'AI Eligibility Triage' },
  { label: 'Evidence', shortLabel: '2', description: 'Evidence & Supporting Details' },
  { label: 'Review', shortLabel: '3', description: 'Metro 2 Technical Review' },
  { label: 'Generate', shortLabel: '4', description: 'Validate & Generate' },
  { label: 'Preview', shortLabel: '5', description: 'Preview & Download' },
];

// ============================================================================
// STEP INDICATOR COMPONENT
// ============================================================================

const StepIndicator = ({ currentStep, steps }) => (
  <div className="flex items-center justify-center mb-8" data-testid="step-indicator">
    {steps.map((step, index) => (
      <React.Fragment key={index}>
        <div className="flex flex-col items-center">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-all ${
              index < currentStep
                ? 'bg-emerald-500 text-white'
                : index === currentStep
                ? 'bg-emerald-600 text-white ring-4 ring-emerald-100 dark:ring-emerald-900/50'
                : 'bg-stone-200 dark:bg-[rgba(142,218,182,0.15)] text-stone-500 dark:text-[#93AE9F]'
            }`}
          >
            {index < currentStep ? (
              <Check className="w-5 h-5" />
            ) : (
              step.shortLabel
            )}
          </div>
          <span className={`mt-2 text-xs font-medium ${
            index <= currentStep 
              ? 'text-emerald-600 dark:text-emerald-400' 
              : 'text-stone-400 dark:text-[#93AE9F]'
          }`}>
            {step.label}
          </span>
        </div>
        {index < steps.length - 1 && (
          <div className={`w-16 h-0.5 mx-2 mt-[-20px] ${
            index < currentStep 
              ? 'bg-emerald-500' 
              : 'bg-stone-200 dark:bg-[rgba(142,218,182,0.15)]'
          }`} />
        )}
      </React.Fragment>
    ))}
  </div>
);

// ============================================================================
// ACCESS DENIED SCREEN
// ============================================================================

const AccessDeniedScreen = ({ reason, onBack, onRequestAccess }) => (
  <div className="min-h-screen bg-[var(--fe-light-bg)] dark:bg-[var(--dark-bg0)] flex items-center justify-center p-6">
    <Card className="max-w-lg w-full bg-white dark:bg-[var(--dark-bg1)] border-amber-400 dark:border-amber-500/30 shadow-lg">
      <CardHeader className="text-center">
        <div className="mx-auto w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center mb-4">
          <Lock className="w-8 h-8 text-amber-600 dark:text-amber-400" />
        </div>
        <CardTitle className="text-xl text-stone-900 dark:text-white">Metro 2 Expert Review</CardTitle>
        <CardDescription className="text-stone-500 dark:text-neutral-400">
          Agency-Only Technical Dispute Review
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-stone-600 dark:text-neutral-300 text-center text-sm">
          Advanced technical review for account reporting errors, coding conflicts, 
          balance/status issues, and bureau-level inconsistencies.
        </p>
        <div className="p-4 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-lg">
          <p className="text-amber-700 dark:text-amber-300 text-sm">{reason}</p>
        </div>
      </CardContent>
      <CardFooter className="flex flex-col gap-2">
        <Button 
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
          onClick={onRequestAccess}
        >
          Request Expert Review
        </Button>
        <Button variant="outline" className="w-full" onClick={onBack}>
          Go Back
        </Button>
      </CardFooter>
    </Card>
  </div>
);

// ============================================================================
// MAIN WIZARD COMPONENT
// ============================================================================

export default function Metro2TechnicalWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token } = useAuth();
  const { reportData, selectedReport } = useReportData();
  
  // ==================== STATE ====================
  
  // Wizard navigation
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Entitlement
  const [hasAccess, setHasAccess] = useState(null);
  const [accessDeniedReason, setAccessDeniedReason] = useState('');
  
  // Step 1: Triage & Selection
  const [triageResult, setTriageResult] = useState(null);
  const [triageLoading, setTriageLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  
  // Step 2: Context (Structured)
  const [structuredContext, setStructuredContext] = useState({});
  const [optionalNote, setOptionalNote] = useState('');
  const [studentLoanContext, setStudentLoanContext] = useState({});
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  
  // Step 3: Review & Package Builder
  const [previewPlan, setPreviewPlan] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [packageRecommendation, setPackageRecommendation] = useState(null);
  const [selectedPackageType, setSelectedPackageType] = useState('ai_recommended');
  const [selectedLetterStyle, setSelectedLetterStyle] = useState('advanced');
  const [maxItemsPerLetter, setMaxItemsPerLetter] = useState('ai_recommended');
  const [includeSignature, setIncludeSignature] = useState(true);
  
  // Step 4 & 5: Generation
  const [generatedLetters, setGeneratedLetters] = useState([]);
  const [generationLoading, setGenerationLoading] = useState(false);
  const [selectedBureauTab, setSelectedBureauTab] = useState('Equifax');
  const [generationStage, setGenerationStage] = useState('');
  
  // ==================== EFFECTS ====================
  
  // ==================== API CALLS ====================
  
  const checkMetro2Access = useCallback(async () => {
    try {
      const userId = user?.user_id || user?.id;
      if (!userId) {
        setHasAccess(true); // Allow access in demo mode
        return;
      }
      
      const response = await fetch(
        `${BACKEND_URL}/api/disputes/metro2/entitlement-check?user_id=${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const data = await response.json();
      setHasAccess(data.has_access);
      if (!data.has_access) {
        setAccessDeniedReason(data.access_reason || 'Metro 2 access denied');
      }
    } catch (err) {
      console.error('Entitlement check failed:', err);
      setHasAccess(true); // Allow access if check fails
    }
  }, [user, token]);
  
  // Check entitlement on mount
  useEffect(() => {
    checkMetro2Access();
  }, [checkMetro2Access]);

  const performTriage = useCallback(async () => {
    setTriageLoading(true);
    try {
      const sourceItems = reportData?.tradelines || reportData?.accounts || [];
      const itemsToTriage = sourceItems.map((account, idx) => ({
        item_id: account.id || account._id || `acc_${Date.now()}_${idx}`,
        item_type: account.item_type || 'account',
        creditor_name: account.creditor_name || account.creditor || account.name || 'Unknown',
        account_number_masked: account.account_number_masked || account.account_number?.slice(-4) || '',
        bureaus: account.bureaus || ['Equifax', 'Experian', 'TransUnion'].filter(b => account[b.toLowerCase()] || account[b]),
        account_status: account.account_status || account.status || '',
        payment_status: account.payment_status || '',
        balance: parseFloat(account.balance) || 0,
        past_due: parseFloat(account.past_due) || 0,
        date_opened: account.date_opened || '',
        dofd: account.dofd || account.date_of_first_delinquency || '',
        is_negative: account.is_negative || account.negative || false,
        is_collection: account.is_collection || (account.account_status || '').toLowerCase().includes('collection'),
        is_charge_off: account.is_charge_off || (account.account_status || '').toLowerCase().includes('charge'),
        bureau_data: account.bureau_data || {},
        raw_data: account,
      }));
      
      const response = await fetch(`${BACKEND_URL}/api/disputes/metro2/triage-items`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          report_id: selectedReport?.id || 'current_report',
          requested_flow: 'metro2',
          selected_items: itemsToTriage,
          user_id: user?.user_id || user?.id,
          ...METRO2_CANONICAL,
        }),
      });
      
      if (!response.ok) {
        throw new Error('Triage failed');
      }
      
      const result = await response.json();
      setTriageResult(result);
      
      // Auto-select AI recommended Metro 2 items
      const autoSelected = [
        ...(result.confirmed_metro2_items || []),
        ...(result.needs_metro2_review_items || []),
        ...(result.metro2_eligible_items || []),
      ].map(item => ({
        ...item,
        selected: item.eligibility_status === 'confirmed_metro2',
      }));
      
      setSelectedItems(autoSelected);
      
    } catch (err) {
      console.error('Triage error:', err);
      toast.error('Failed to analyze items for Metro 2 eligibility');
    } finally {
      setTriageLoading(false);
    }
  }, [reportData, selectedReport, token, user]);

  // Auto-triage when report data is available
  useEffect(() => {
    const items = reportData?.tradelines || reportData?.accounts || [];
    if (items.length > 0 && hasAccess) {
      performTriage();
    }
  }, [reportData, hasAccess, performTriage]);

  const fetchPackageRecommendation = async () => {
    try {
      const eligibleItems = selectedItems.filter(i => i.selected);
      const response = await fetch(`${BACKEND_URL}/api/disputes/metro2/recommend-letter-package`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          report_id: selectedReport?.id || 'current_report',
          selected_items: eligibleItems,
          selected_issue_categories: eligibleItems.flatMap(i => i.suggested_issue_categories || []),
          bureaus: [...new Set(eligibleItems.flatMap(i => i.bureaus || []))],
          dispute_round: 1,
          ...METRO2_CANONICAL,
        }),
      });
      
      if (response.ok) {
        const recommendation = await response.json();
        setPackageRecommendation(recommendation);
        setSelectedPackageType(recommendation.recommended_letter_type);
        setSelectedLetterStyle(recommendation.recommended_letter_style);
      }
    } catch (err) {
      console.error('Package recommendation error:', err);
    }
  };
  
  const fetchPreviewPlan = async () => {
    setPreviewLoading(true);
    setError(null);
    
    try {
      const eligibleItems = selectedItems.filter(i => i.selected);
      
      if (eligibleItems.length === 0) {
        throw new Error('No items selected for Metro 2 dispute');
      }
      
      const disputeItems = eligibleItems.map(item => ({
        item_id: item.item_id,
        item_type: 'account',
        creditor_name: item.creditor_name,
        account_number_masked: item.account_number_masked || '****',
        bureaus: item.bureaus || ['Equifax', 'Experian', 'TransUnion'],
        issue_category: item.suggested_issue_categories?.[0] || 'account_status_conflict',
        reported_values: item.raw_data?.reported_values || { account_status: item.account_status, balance: item.balance },
        expected_values: {},
        conflict_description: item.reason || 'Reporting inconsistency detected',
      }));
      
      const response = await fetch(`${BACKEND_URL}/api/disputes/metro2/preview-generation-plan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          dispute_items: disputeItems,
          bureaus: ['Equifax', 'Experian', 'TransUnion'],
          round_number: 1,
          user_id: user?.user_id || user?.id,
          structured_context: structuredContext,
          consumer_supporting_note: optionalNote,
          ...METRO2_CANONICAL,
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail?.message || errorData.detail || 'Failed to preview plan');
      }
      
      const plan = await response.json();
      setPreviewPlan(plan);
      
      if (plan.bureau_coverage?.length > 0) {
        setSelectedBureauTab(plan.bureau_coverage[0]);
      }
      
      // Also fetch package recommendation
      await fetchPackageRecommendation();
      
    } catch (err) {
      console.error('Preview plan error:', err);
      setError(err.message);
      toast.error(err.message);
    } finally {
      setPreviewLoading(false);
    }
  };
  
  const generateLetters = async () => {
    if (!previewPlan) {
      toast.error('Please complete the review step first');
      return;
    }
    
    setGenerationLoading(true);
    setError(null);
    
    // Generation stages
    const stages = [
      'Validating Metro 2 eligibility',
      'Validating method = metro2_technical',
      'Validating generation guardrails',
      'Building field-logic tables',
      'Building monthly payment-history review',
      'Attaching legal authority blocks',
      'Attaching Metro 2 field mappings',
      'Preparing bureau-specific packages',
      'Applying letter tone',
      'Generating Metro 2 letters',
      'Validating preview/PDF parity',
      'Finalizing',
    ];
    
    try {
      // Show stages progressively
      for (let i = 0; i < stages.length - 2; i++) {
        setGenerationStage(stages[i]);
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      setGenerationStage('Generating Metro 2 letters');
      
      const response = await fetch(`${BACKEND_URL}/api/disputes/metro2/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          preview_plan: previewPlan,
          consumer_info: {
            full_name: user?.full_name || user?.name || 'Consumer',
            address: user?.address || '',
            city: user?.city || '',
            state: user?.state || '',
            zip: user?.zip || '',
            ssn_last_4: user?.ssn_last_4 || '',
            dob: user?.dob || '',
          },
          user_id: user?.user_id || user?.id,
          template_family: 'metro2',
          letter_type: selectedPackageType,
          letter_style: selectedLetterStyle,
          ...METRO2_CANONICAL,
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to generate letters');
      }
      
      setGenerationStage('Finalizing');
      const result = await response.json();
      setGeneratedLetters(result.letters || []);
      toast.success(`Generated ${result.total_letters} Metro 2 letter(s)`);
      
    } catch (err) {
      console.error('Generation error:', err);
      setError(err.message);
      toast.error(err.message);
    } finally {
      setGenerationLoading(false);
      setGenerationStage('');
    }
  };
  
  // ==================== HANDLERS ====================
  
  const handleItemToggle = useCallback((itemId) => {
    setSelectedItems(prev => 
      prev.map(item => 
        item.item_id === itemId 
          ? { ...item, selected: !item.selected }
          : item
      )
    );
  }, []);
  
  const handleSelectAIRecommended = useCallback(() => {
    setSelectedItems(prev => 
      prev.map(item => ({
        ...item,
        selected: item.eligibility_status === 'confirmed_metro2' || 
                  item.eligibility_status === 'needs_metro2_review',
      }))
    );
    toast.success('AI recommended items selected');
  }, []);
  
  const handleSelectAllEligible = useCallback(() => {
    setSelectedItems(prev => 
      prev.map(item => ({
        ...item,
        selected: item.eligibility_status === 'confirmed_metro2' || 
                  item.eligibility_status === 'needs_metro2_review' ||
                  item.eligibility_status === 'metro2_eligible',
      }))
    );
    toast.success('All Metro 2 eligible items selected');
  }, []);
  
  const handleApplyAIRecommendation = useCallback(() => {
    if (packageRecommendation) {
      setSelectedPackageType(packageRecommendation.recommended_letter_type);
      setSelectedLetterStyle(packageRecommendation.recommended_letter_style);
      setMaxItemsPerLetter(packageRecommendation.recommended_account_limit || 'ai_recommended');
      toast.success('AI recommendation applied');
    }
  }, [packageRecommendation]);
  
  const handleNext = async () => {
    if (currentStep === 0) {
      const selectedCount = selectedItems.filter(i => i.selected).length;
      if (selectedCount === 0) {
        toast.error('Please select at least one item for Metro 2 dispute');
        return;
      }
      
      // Check for mixed selection (Metro 2 + non-Metro 2)
      const nonMetro2Items = selectedItems.filter(i => 
        i.selected && 
        !['confirmed_metro2', 'needs_metro2_review', 'metro2_eligible'].includes(i.eligibility_status)
      );
      
      if (nonMetro2Items.length > 0) {
        toast.warning(`${nonMetro2Items.length} non-Metro 2 items will be routed to appropriate flows`);
      }
    }
    
    if (currentStep === 1) {
      await fetchPreviewPlan();
    }
    
    if (currentStep === 3) {
      await generateLetters();
    }
    
    if (currentStep < 4) {
      setCurrentStep(prev => prev + 1);
    }
  };
  
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };
  
  // ==================== COMPUTED VALUES ====================
  
  const selectedCount = useMemo(() => 
    selectedItems.filter(i => i.selected).length, 
    [selectedItems]
  );
  
  const eligibleSelectedItems = useMemo(() => 
    selectedItems.filter(i => 
      i.selected && 
      ['confirmed_metro2', 'needs_metro2_review', 'metro2_eligible'].includes(i.eligibility_status)
    ),
    [selectedItems]
  );
  
  // Detect special account types in selection
  const hasCollections = useMemo(() => 
    eligibleSelectedItems.some(i => i.is_collection || (i.account_status || '').toLowerCase().includes('collection')),
    [eligibleSelectedItems]
  );
  
  const hasChargeOffs = useMemo(() => 
    eligibleSelectedItems.some(i => i.is_charge_off || (i.account_status || '').toLowerCase().includes('charge')),
    [eligibleSelectedItems]
  );
  
  const hasStudentLoans = useMemo(() => 
    eligibleSelectedItems.some(i => i.is_student_loan || (i.account_type || '').toLowerCase().includes('student')),
    [eligibleSelectedItems]
  );
  
  // ==================== RENDER ACCESS CHECK ====================
  
  if (hasAccess === false) {
    return (
      <AccessDeniedScreen 
        reason={accessDeniedReason}
        onBack={() => navigate(-1)}
        onRequestAccess={() => navigate('/contact')}
      />
    );
  }
  
  if (hasAccess === null) {
    return (
      <div className="min-h-screen bg-[var(--fe-light-bg)] dark:bg-[var(--dark-bg0)] flex items-center justify-center">
        <div className="flex items-center gap-3 text-stone-500 dark:text-neutral-400">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Checking access...</span>
        </div>
      </div>
    );
  }
  
  // ==================== MAIN RENDER ====================
  
  return (
    <div className="min-h-screen bg-[var(--fe-light-bg)] dark:bg-[var(--dark-bg0)]" data-testid="metro2-technical-wizard">
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-500/20">
              <Database className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-stone-900 dark:text-white">Metro 2 Technical Dispute</h1>
              <p className="text-sm text-stone-500 dark:text-neutral-400">Field-level reporting analysis</p>
            </div>
            <Badge className="ml-auto bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-500/30" data-testid="metro2-badge">
              family: metro2 | method: metro2_technical
            </Badge>
          </div>
        </div>

        {/* Step Indicator */}
        <StepIndicator currentStep={currentStep} steps={STEPS} />

        {/* Main Content */}
        <AnimatePresence mode="wait">
          {/* ==================== STEP 1: AI ELIGIBILITY TRIAGE ==================== */}
          {currentStep === 0 && (
            <motion.div
              key="step0"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
              data-testid="step-1-triage"
            >
              <Card className="bg-white dark:bg-[var(--dark-bg1)] border-stone-200 dark:border-[var(--dark-border)]">
                <CardHeader>
                  <CardTitle className="text-lg text-stone-900 dark:text-white flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                    AI Eligibility Triage + Account Selection
                  </CardTitle>
                  <CardDescription className="text-stone-500 dark:text-neutral-400">
                    AI has analyzed your credit report for field-level Metro 2 reporting issues. 
                    Items recommended for Metro 2 contain technical reporting patterns that may require verification.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Metro2EligibilityTriage
                    triageResult={triageResult}
                    loading={triageLoading}
                    selectedItems={selectedItems}
                    onItemSelect={handleItemToggle}
                    onSelectAIRecommended={handleSelectAIRecommended}
                    onSelectAllEligible={handleSelectAllEligible}
                    onContinue={handleNext}
                    onNavigateToFactual={() => navigate('/self-repair/disputes/factual')}
                    onNavigateToInquiry={() => navigate('/self-repair/disputes/factual')}
                    onNavigateToPI={() => navigate('/self-repair/disputes/factual')}
                  />
                </CardContent>
              </Card>

              {/* Routed Outside Metro 2 */}
              {triageResult && (
                <RoutedDisputeItemsPanel
                  inquiryItems={triageResult.inquiry_items || []}
                  personalInfoItems={triageResult.personal_info_items || []}
                  dataBreachItems={triageResult.data_breach_candidates || []}
                  factualItems={triageResult.factual_eligible_items || []}
                  evidenceNeededItems={triageResult.evidence_needed_items || []}
                  onNavigate={(path) => navigate(path)}
                />
              )}
            </motion.div>
          )}

          {/* ==================== STEP 2: EVIDENCE & SUPPORTING DETAILS ==================== */}
          {currentStep === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
              data-testid="step-2-evidence"
            >
              <Card className="bg-white dark:bg-[var(--dark-bg1)] border-stone-200 dark:border-[var(--dark-border)]">
                <CardHeader>
                  <CardTitle className="text-lg text-stone-900 dark:text-white flex items-center gap-2">
                    <FileText className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                    Evidence & Supporting Details
                  </CardTitle>
                  <CardDescription className="text-stone-500 dark:text-neutral-400">
                    Review the selected items and add supporting evidence only if you have it. 
                    The system will build the Metro 2 technical dispute from the detected reporting patterns.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Metro2Step2Evidence
                    selectedItems={eligibleSelectedItems}
                    contextAnswers={structuredContext}
                    onContextAnswerChange={setStructuredContext}
                    optionalNote={optionalNote}
                    onOptionalNoteChange={setOptionalNote}
                    selectedDocuments={uploadedDocuments}
                    onDocumentSelect={(docs) => setUploadedDocuments(docs)}
                    onUploadClick={() => toast.info('Document upload coming soon')}
                  />
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* ==================== STEP 3: METRO 2 TECHNICAL REVIEW ==================== */}
          {currentStep === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
              data-testid="step-3-review"
            >
              {previewLoading ? (
                <Card className="bg-white dark:bg-[var(--dark-bg1)] border-stone-200 dark:border-[var(--dark-border)]">
                  <CardContent className="flex items-center justify-center py-16">
                    <RefreshCw className="w-8 h-8 animate-spin text-emerald-600 dark:text-emerald-400 mr-3" />
                    <span className="text-stone-500 dark:text-neutral-400">Building Metro 2 technical review...</span>
                  </CardContent>
                </Card>
              ) : previewPlan ? (
                <>
                  {/* AI Package Builder */}
                  <Card className="bg-white dark:bg-[var(--dark-bg1)] border-stone-200 dark:border-[var(--dark-border)]">
                    <CardHeader>
                      <CardTitle className="text-lg text-stone-900 dark:text-white flex items-center gap-2">
                        <Zap className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                        Metro 2 Letter Package Builder
                      </CardTitle>
                      <CardDescription className="text-stone-500 dark:text-neutral-400">
                        Select your Metro 2 letter package type. Professional, legal, Metro 2-specific packages only.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Metro2LetterPackageBuilder
                        recommendation={packageRecommendation}
                        selectedPackageType={selectedPackageType}
                        selectedLetterStyle={selectedLetterStyle}
                        maxItemsPerLetter={maxItemsPerLetter}
                        includeSignature={includeSignature}
                        onPackageTypeChange={setSelectedPackageType}
                        onLetterStyleChange={setSelectedLetterStyle}
                        onMaxItemsChange={setMaxItemsPerLetter}
                        onSignatureChange={setIncludeSignature}
                        onApplyAIRecommendation={handleApplyAIRecommendation}
                        onRestoreAIRecommendation={handleApplyAIRecommendation}
                      />
                    </CardContent>
                  </Card>

                  {/* Step 2 Context Summary */}
                  <Card className="bg-white dark:bg-[var(--dark-bg1)] border-stone-200 dark:border-[var(--dark-border)]" data-testid="step2-context-summary">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg text-stone-900 dark:text-white flex items-center gap-2">
                        <FileText className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                        Supporting Context from Step 2
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Show context summary from backend */}
                      {previewPlan.context_summary ? (
                        <div className="p-4 bg-purple-50 dark:bg-purple-500/10 rounded-lg border border-purple-200 dark:border-purple-500/30">
                          <p className="text-sm text-purple-700 dark:text-purple-300">{previewPlan.context_summary}</p>
                        </div>
                      ) : (
                        <div className="p-4 bg-stone-50 dark:bg-stone-800/50 rounded-lg">
                          <p className="text-sm text-stone-500 dark:text-stone-400">
                            No additional context provided. The Metro 2 dispute will be built from detected field-level reporting patterns.
                          </p>
                        </div>
                      )}
                      
                      {/* Evidence Status */}
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-stone-600 dark:text-stone-400">Evidence:</span>
                        {previewPlan.evidence_info?.evidence_status === 'provided' ? (
                          <Badge className="bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Documents Attached
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-stone-500">
                            No documents attached
                          </Badge>
                        )}
                      </div>
                      
                      {/* Optional Note Indicator */}
                      {previewPlan.consumer_supporting_note && (
                        <div className="p-3 bg-amber-50 dark:bg-amber-500/10 rounded-lg border border-amber-200 dark:border-amber-500/30">
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-xs font-medium text-amber-700 dark:text-amber-300">Optional Note Provided</p>
                              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                                Your optional note will be processed safely. Raw notes are never pasted directly into the letter.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Bureau Coverage */}
                  <Card className="bg-white dark:bg-[var(--dark-bg1)] border-stone-200 dark:border-[var(--dark-border)]">
                    <CardHeader>
                      <CardTitle className="text-lg text-stone-900 dark:text-white flex items-center gap-2">
                        <Table className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                        Bureau Coverage & Technical Review
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Bureau Badges */}
                      <div className="flex gap-2">
                        {previewPlan.bureau_coverage?.map(bureau => (
                          <Badge key={bureau} className={BUREAU_COLORS[bureau]}>
                            {bureau} - Metro 2
                          </Badge>
                        ))}
                      </div>

                      {/* Technical Issue Summary */}
                      {previewPlan.technical_issue_summary?.length > 0 && (
                        <div className="space-y-2">
                          <h4 className="font-medium text-stone-700 dark:text-stone-300">Technical Issue Summary</h4>
                          {previewPlan.technical_issue_summary.map((issue, idx) => (
                            <div 
                              key={idx}
                              className="flex items-center justify-between p-3 bg-stone-50 dark:bg-[var(--dark-bg2)] rounded-lg"
                            >
                              <div className="flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                                <span className="text-stone-900 dark:text-white">{issue.category_label}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline">{issue.item_count} item(s)</Badge>
                                <span className="text-xs text-stone-500 dark:text-neutral-500">
                                  {issue.fields_affected?.join(', ')}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Package Estimate */}
                      <div className="p-4 bg-emerald-50 dark:bg-emerald-500/10 rounded-lg border border-emerald-200 dark:border-emerald-500/30">
                        <h4 className="font-medium text-emerald-900 dark:text-emerald-300 mb-3">Package Estimate</h4>
                        <div className="grid grid-cols-3 gap-4 text-center">
                          <div>
                            <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                              {previewPlan.total_letters}
                            </div>
                            <div className="text-xs text-emerald-700 dark:text-emerald-400">Letters</div>
                          </div>
                          <div>
                            <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                              {previewPlan.total_items}
                            </div>
                            <div className="text-xs text-emerald-700 dark:text-emerald-400">Items</div>
                          </div>
                          <div>
                            <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                              {previewPlan.bureau_coverage?.length || 0}
                            </div>
                            <div className="text-xs text-emerald-700 dark:text-emerald-400">Bureaus</div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Special Review Panels */}
                  {hasCollections && eligibleSelectedItems.filter(i => 
                    i.is_collection || (i.account_status || '').toLowerCase().includes('collection')
                  ).map((item, idx) => (
                    <Metro2CollectionReviewPanel
                      key={`collection-${idx}`}
                      account={item}
                      detectedIssues={item.detected_issues || []}
                    />
                  ))}

                  {hasChargeOffs && eligibleSelectedItems.filter(i => 
                    i.is_charge_off || (i.account_status || '').toLowerCase().includes('charge')
                  ).map((item, idx) => (
                    <Metro2ChargeOffReviewPanel
                      key={`chargeoff-${idx}`}
                      account={item}
                      detectedIssues={item.detected_issues || []}
                    />
                  ))}

                  {hasStudentLoans && eligibleSelectedItems.filter(i => 
                    i.is_student_loan || (i.account_type || '').toLowerCase().includes('student')
                  ).map((item, idx) => (
                    <Metro2StudentLoanPanel
                      key={`student-${idx}`}
                      account={item}
                      context={studentLoanContext}
                      detectedIssues={item.detected_issues || []}
                      showContextQuestions={false}
                    />
                  ))}

                  {/* Legal Basis Summary */}
                  <Metro2LegalBasisSummary
                    authorities={previewPlan.legal_authorities}
                    issueCategories={previewPlan.technical_issue_summary?.map(i => i.category) || []}
                  />

                  {/* Field Mapping Summary */}
                  <Metro2FieldMappingSummary
                    fieldMappings={previewPlan.field_mappings}
                    conflictFields={previewPlan.field_level_breakdown?.map(f => f.field_code) || []}
                  />

                  {/* Metro 2 Isolation Validation */}
                  <Card className="bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/30">
                    <CardContent className="py-4">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-emerald-700 dark:text-emerald-400">Metro 2 Isolation Verified</h4>
                          <ul className="mt-2 space-y-1 text-sm text-emerald-600 dark:text-emerald-300/80">
                            <li>No factual packages detected</li>
                            <li>No data breach packages detected</li>
                            <li>No structural/recovery/isolated labels</li>
                            <li>All items are Metro 2 eligible accounts</li>
                            <li>Method: metro2_technical | Family: metro2</li>
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </>
              ) : error ? (
                <Alert className="bg-red-50 dark:bg-red-500/10 border-red-300 dark:border-red-500/30">
                  <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                  <AlertTitle className="text-red-700 dark:text-red-400">Error</AlertTitle>
                  <AlertDescription className="text-red-600 dark:text-red-300">{error}</AlertDescription>
                </Alert>
              ) : null}
            </motion.div>
          )}

          {/* ==================== STEP 4: GENERATE ==================== */}
          {currentStep === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
              data-testid="step-4-generate"
            >
              <Card className="bg-white dark:bg-[var(--dark-bg1)] border-stone-200 dark:border-[var(--dark-border)]">
                <CardHeader>
                  <CardTitle className="text-lg text-stone-900 dark:text-white">
                    {generationLoading ? 'Generating Metro 2 Letters' : 'Generation Complete'}
                  </CardTitle>
                  <CardDescription className="text-stone-500 dark:text-neutral-400">
                    {generationLoading 
                      ? 'Creating technical dispute letters with violation tables.'
                      : `Generated ${generatedLetters.length} Metro 2 letter(s).`
                    }
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {generationLoading ? (
                    <div className="text-center py-12">
                      <RefreshCw className="w-12 h-12 animate-spin text-emerald-600 dark:text-emerald-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-stone-900 dark:text-white mb-2">{generationStage || 'Generating...'}</h3>
                      <p className="text-stone-500 dark:text-neutral-400">
                        Using Metro 2 technical template with violation tables
                      </p>
                      {/* Generation Stages */}
                      <div className="mt-6 max-w-md mx-auto">
                        <div className="h-2 bg-stone-200 dark:bg-stone-700 rounded-full overflow-hidden">
                          <div className="h-full bg-emerald-500 transition-all duration-300" style={{ width: '60%' }} />
                        </div>
                      </div>
                    </div>
                  ) : generatedLetters.length > 0 ? (
                    <div className="space-y-4">
                      <Alert className="bg-emerald-50 dark:bg-emerald-500/10 border-emerald-300 dark:border-emerald-500/30">
                        <CheckCircle className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                        <AlertTitle className="text-emerald-700 dark:text-emerald-400">Generation Complete</AlertTitle>
                        <AlertDescription className="text-emerald-600 dark:text-emerald-300">
                          Generated {generatedLetters.length} Metro 2 technical dispute letter(s). 
                          Method: metro2_technical | Family: metro2
                        </AlertDescription>
                      </Alert>
                      
                      <div className="grid gap-3">
                        {generatedLetters.map((letter, idx) => (
                          <div 
                            key={idx}
                            className="flex items-center justify-between p-4 bg-stone-50 dark:bg-[var(--dark-bg2)] rounded-lg"
                          >
                            <div className="flex items-center gap-3">
                              <FileText className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                              <div>
                                <h4 className="font-medium text-stone-900 dark:text-white">{letter.bureau} - Metro 2</h4>
                                <p className="text-sm text-stone-500 dark:text-neutral-400">
                                  {letter.item_count} item(s) | Violation table included
                                </p>
                              </div>
                            </div>
                            <Badge className="bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400">
                              Ready
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : error ? (
                    <Alert className="bg-red-50 dark:bg-red-500/10 border-red-300 dark:border-red-500/30">
                      <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                      <AlertTitle className="text-red-700 dark:text-red-400">Generation Failed</AlertTitle>
                      <AlertDescription className="text-red-600 dark:text-red-300">{error}</AlertDescription>
                    </Alert>
                  ) : null}
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* ==================== STEP 5: PREVIEW ==================== */}
          {currentStep === 4 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
              data-testid="step-5-preview"
            >
              <Card className="bg-white dark:bg-[var(--dark-bg1)] border-stone-200 dark:border-[var(--dark-border)]">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg text-stone-900 dark:text-white">Metro 2 Letter Preview</CardTitle>
                      <CardDescription className="text-stone-500 dark:text-neutral-400">
                        Review and download your Metro 2 technical dispute letters.
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm">
                        <Printer className="w-4 h-4 mr-2" />
                        Print
                      </Button>
                      <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" size="sm">
                        <Download className="w-4 h-4 mr-2" />
                        Download PDF
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {generatedLetters.length > 0 ? (
                    <Tabs value={selectedBureauTab} onValueChange={setSelectedBureauTab}>
                      <TabsList className="mb-4">
                        {generatedLetters.map(letter => (
                          <TabsTrigger key={letter.bureau} value={letter.bureau}>
                            {letter.bureau} - Metro 2
                          </TabsTrigger>
                        ))}
                      </TabsList>
                      
                      {generatedLetters.map(letter => (
                        <TabsContent key={letter.bureau} value={letter.bureau}>
                          <div className="border border-stone-200 dark:border-[var(--dark-border)] rounded-lg p-6">
                            <div className="flex items-center justify-between mb-4 pb-4 border-b border-stone-200 dark:border-[var(--dark-border)]">
                              <div className="flex items-center gap-2">
                                <FileText className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                                <span className="font-medium text-stone-900 dark:text-white">
                                  {letter.bureau} Metro 2 Technical Dispute
                                </span>
                              </div>
                              <div className="flex gap-2">
                                <Badge className="bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400">
                                  {letter.item_count} Items
                                </Badge>
                                <Badge variant="outline" className="border-emerald-400 dark:border-emerald-500/50 text-emerald-600 dark:text-emerald-400">
                                  Violation Table
                                </Badge>
                              </div>
                            </div>
                            <div 
                              className="bg-white text-black p-8 rounded-lg max-h-[600px] overflow-y-auto prose prose-sm"
                              dangerouslySetInnerHTML={{ __html: letter.letter_html }}
                            />
                          </div>
                        </TabsContent>
                      ))}
                    </Tabs>
                  ) : (
                    <div className="text-center py-12">
                      <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-stone-600 dark:text-neutral-400">No Letters Generated</h3>
                      <p className="text-sm text-stone-500 dark:text-neutral-500 mt-2">
                        Please go back and complete the generation step.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Additional Dispute Opportunities */}
              {triageResult?.factual_eligible_items?.length > 0 && (
                <Card className="bg-amber-50 dark:bg-amber-500/10 border-amber-300 dark:border-amber-500/30">
                  <CardHeader>
                    <CardTitle className="text-lg text-amber-900 dark:text-amber-300">
                      Additional Dispute Opportunities Found
                    </CardTitle>
                    <CardDescription className="text-amber-700 dark:text-amber-400">
                      {triageResult.factual_eligible_items.length} item(s) were saved for Factual Dispute. 
                      You can generate those letters next.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => navigate('/self-repair/disputes/factual')}>
                        Generate Factual Dispute Letters
                      </Button>
                      <Button variant="ghost">
                        View Routed Items
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation Buttons */}
        <div className="mt-8 flex items-center justify-between pt-6 border-t border-stone-200 dark:border-[var(--dark-border)]">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 0}
            className="disabled:opacity-50"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <div className="flex items-center gap-4">
            {/* Selection Summary */}
            {currentStep === 0 && selectedCount > 0 && (
              <span className="text-sm text-emerald-600 dark:text-emerald-400">
                {selectedCount} item(s) selected
              </span>
            )}
            
            <Button
              onClick={handleNext}
              disabled={
                loading || 
                triageLoading || 
                previewLoading || 
                generationLoading ||
                (currentStep === 0 && selectedCount === 0) ||
                (currentStep === 4)
              }
              className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
              data-testid="next-button"
            >
              {currentStep === 3 ? (
                generationLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : generatedLetters.length > 0 ? (
                  <>
                    View Preview
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                ) : (
                  <>
                    Generate Letters
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )
              ) : currentStep === 4 ? (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download All
                </>
              ) : currentStep === 1 ? (
                <>
                  Continue to Technical Review
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              ) : (
                <>
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
